/**
 * Пометки строк «Голоса клиента» — «выросло», «упало», «новое», «исчезло»
 * (#847 §4, решение владельца Р3 от 04.10; #2394 §9 п. 6). Одно правило на
 * экран, печатную форму и ежемесячное письмо: письмо обязано сообщать то же,
 * что экран, а исправить его после отправки нельзя.
 *
 * Пометка — вывод, а не разность чисел: она ставится, только когда выборка
 * её держит, и молчит при любом сомнении в данных (окно упёрлось в потолок,
 * период покрыт не целиком, набор кодов сменился). Молчание — не «без
 * изменений»: числа строки остаются на месте, нет только вывода.
 *
 * Вход — счёт строки по периодам; какой период покрыт, решает вызывающий
 * (`VoiceMarkPeriods` из `@aica/database`): у него срок хранения, первая
 * запись и покрытие фактами.
 */

/** В обоих периодах столько разговоров блока (N) и больше — иначе роста и падения нет. */
export const VOICE_MARK_MIN_CALLS_IN_WINDOW = 30;

/** В большем из двух периодов столько разговоров со строкой (K) и больше. */
export const VOICE_MARK_MIN_CALLS = 5;

/** Сдвиг доли не меньше стольких процентных пунктов… */
export const VOICE_MARK_MIN_DELTA_PP = 3;

/** …и не меньше этой доли от прошлой доли (50 % — «в полтора раза»). */
export const VOICE_MARK_MIN_RELATIVE = 0.5;

/**
 * «Новое» — сейчас K не меньше этого; «исчезло» — K не меньше этого был в
 * каждом покрытом прошлом периоде.
 */
export const VOICE_MARK_PRESENCE_CALLS = 3;

/** Счёт строки за период: K из N. */
export interface VoicePeriodCount {
  /** K: разговоры периода со строкой. */
  calls: number;
  /** N: разговоры периода, где блок строки собирался. */
  callsInWindow: number;
}

export interface VoiceMarkInput {
  current: VoicePeriodCount;
  /**
   * Прошлый период той же длины; `null` — он не покрыт: срок хранения обрезал
   * его начало, данных организации ещё не было, его счёт неполон (потолок
   * скана, факты не заполнены). Без него пометки нет никакой.
   */
  previous: VoicePeriodCount | null;
  /**
   * Периоды той же длины раньше прошлого, в пределах горизонта (срок
   * хранения, не дальше года), — все, где у области были данные. `[]` —
   * таких нет (горизонт или начало данных — сразу за прошлым периодом).
   * `null` — хоть один из них не покрыт или не прочитан: «новое» и «исчезло»
   * — утверждения обо всей истории, и по её части их не делают.
   */
  earlier: readonly VoicePeriodCount[] | null;
  /** Текущее окно упёрлось в потолок скана: K — нижняя граница. */
  truncated: boolean;
  /** Набор кодов строки сменился между сравниваемыми периодами. */
  versionBreak: boolean;
}

export type VoiceMark =
  | { kind: 'none' }
  | { kind: 'up' | 'down'; share: number; prevShare: number; delta: number }
  | { kind: 'new' }
  | { kind: 'gone' };

export const VOICE_NO_MARK: VoiceMark = { kind: 'none' };

/** Целый процент; 0 из 0 — ноль, а не NaN. */
function wholePercent(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

/**
 * Значим ли сдвиг доли K/N → K'/N' (Р3): в обоих периодах N ≥ 30, в
 * большем K ≥ 5, сдвиг ≥ 3 п.п. и ≥ 50 % прошлой доли. В целых числах, без
 * округления долей: порог не должен зависеть от того, как округлили проценты.
 */
function significantShift(now: VoicePeriodCount, before: VoicePeriodCount): boolean {
  if (
    now.callsInWindow < VOICE_MARK_MIN_CALLS_IN_WINDOW ||
    before.callsInWindow < VOICE_MARK_MIN_CALLS_IN_WINDOW
  )
    return false;
  if (Math.max(now.calls, before.calls) < VOICE_MARK_MIN_CALLS) return false;
  // |K/N − K'/N'| = |K·N' − K'·N| / (N·N')
  const cross = Math.abs(
    now.calls * before.callsInWindow - before.calls * now.callsInWindow
  );
  const area = now.callsInWindow * before.callsInWindow;
  return (
    cross * 100 >= VOICE_MARK_MIN_DELTA_PP * area &&
    cross >= VOICE_MARK_MIN_RELATIVE * before.calls * now.callsInWindow
  );
}

/**
 * Пометка строки (Р3):
 *
 * - `new` — строки не было ни в одном покрытом прошлом периоде (не меньше
 *   одного), сейчас K ≥ 3;
 * - `gone` — строка была в каждом покрытом прошлом периоде (не меньше
 *   одного) с K ≥ 3, сейчас 0;
 * - `up` / `down` — сдвиг доли к прошлому периоду значим (`significantShift`);
 * - при потолке скана, непокрытом прошлом периоде или разрыве версий —
 *   ничего.
 *
 * Период без разговоров блока (N = 0) о строке ничего не говорит и в
 * историю не входит; текущее окно без них — тоже: «исчезло» из окна, где
 * ничего не измерялось, было бы ложью.
 */
export function voiceMark(input: VoiceMarkInput): VoiceMark {
  const { current, previous } = input;
  if (input.truncated || input.versionBreak || previous === null) return VOICE_NO_MARK;
  if (current.callsInWindow === 0) return VOICE_NO_MARK;

  if (input.earlier !== null) {
    const history = [previous, ...input.earlier].filter((p) => p.callsInWindow > 0);
    if (history.length > 0) {
      if (
        current.calls >= VOICE_MARK_PRESENCE_CALLS &&
        history.every((p) => p.calls === 0)
      )
        return { kind: 'new' };
      if (
        current.calls === 0 &&
        history.every((p) => p.calls >= VOICE_MARK_PRESENCE_CALLS)
      )
        return { kind: 'gone' };
    }
  }

  if (!significantShift(current, previous)) return VOICE_NO_MARK;
  const share = wholePercent(current.calls, current.callsInWindow);
  const prevShare = wholePercent(previous.calls, previous.callsInWindow);
  const up =
    current.calls * previous.callsInWindow > previous.calls * current.callsInWindow;
  return { kind: up ? 'up' : 'down', share, prevShare, delta: share - prevShare };
}

/**
 * Строки списка — карточки дашборда и раздела письма: с разговорами в окне,
 * больше разговоров первыми (равные — в порядке источника), не больше
 * `limit`; в свободные слоты после них — строки без разговоров, у которых
 * есть пометка («исчезло» или «упало» до нуля). Экран, письмо и отчёт
 * говорят о строке одно и то же, а услышанную строку пометка не вытесняет.
 *
 * `moreRows` — сколько не показано: услышанные сверх лимита (`heard` — их
 * число, когда источник сам обрезал список) и помеченные, которым не
 * хватило слота. `moreHeardRows` — только услышанные сверх лимита: при
 * выключенном сравнении помеченных строк без разговоров список не
 * показывает, и «ещё N» считает только то, что зритель может открыть (#2533).
 */
export function voiceListRows<T extends { calls: number; mark?: { kind: string } }>(
  rows: readonly T[],
  limit: number,
  heard?: number
): { rows: T[]; moreRows: number; moreHeardRows: number } {
  const present = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => row.calls > 0)
    .sort((a, b) => b.row.calls - a.row.calls || a.index - b.index)
    .map(({ row }) => row);
  const shown = present.slice(0, limit);
  const marked = rows.filter(
    (row) => row.calls === 0 && row.mark !== undefined && row.mark.kind !== 'none'
  );
  const free = marked.slice(0, Math.max(0, limit - shown.length));
  const moreHeardRows = Math.max(0, (heard ?? present.length) - shown.length);
  return {
    rows: [...shown, ...free],
    moreRows: moreHeardRows + marked.length - free.length,
    moreHeardRows,
  };
}
